// Receiving on Jupnet as a destination of its own: the tokens a user can receive there, and the
// route each goes through (the chain whose gum bank the solver deposits into, `delivery:
// gum_deposit`). The user picks "Jupnet" and a token; the route is chosen here: by quote when
// several can deliver, the token's home chain on a near tie, never the paying chain (the api
// refuses a source and destination that are the same chain).

import type { Chain, ChainKey, JupnetRoute, Token } from "./chains";

/** A token as received on Jupnet, with every route that delivers it. USDC from any chain credits
 *  the one unified USD (gum's relayer binds it, `jupnet::UNIFIED_USD_BOUND`); any other token is
 *  credited as its bridged mint from the route's chain (`GET /jupnet/mint`), so ETH via Base and
 *  ETH via Arbitrum are two Jupnet mints of the same asset. */
export type JupnetAsset = {
  id: string;
  sym: string;
  name: string;
  /** every route credits the same Jupnet mint */
  unified: boolean;
  home?: ChainKey;
  /** home chain first, then the cheaper chains to deposit on */
  routes: { chain: Chain; token: Token }[];
};

/** A token's native chain: preferred on a near tie (its bridged mint is the canonical one). */
export const HOME: Record<string, ChainKey> = { ETH: "ethereum", SOL: "solana", BNB: "bsc", USDT: "ethereum", USDG: "robinhood", cbBTC: "base" };
/** After the home chain: the chains where a deposit costs the solver least. */
const ROUTE_ORDER: ChainKey[] = ["base", "arbitrum", "solana", "robinhood", "bsc", "ethereum"];
/** What a list shows first: the dollar, then the majors. */
const ASSET_ORDER = ["USD", "ETH", "SOL", "USDT", "USDG", "cbBTC", "BNB"];
/** A home-chain route within this many bps of the best quote is taken over it. */
export const HOME_TOLERANCE_BPS = 10;

/** The Jupnet asset a deposited token credits: USDC is unified USD, anything else itself. */
export const assetIdOf = (t: Pick<Token, "sym">): string => (t.sym === "USDC" ? "USD" : t.sym);

const rank = (home: ChainKey | undefined, c: ChainKey) => (c === home ? -1 : ROUTE_ORDER.indexOf(c) < 0 ? ROUTE_ORDER.length : ROUTE_ORDER.indexOf(c));

/** Every token receivable on Jupnet, grouped across the routes `GET /jupnet` offers. */
export function jupnetAssets(routes: JupnetRoute[]): JupnetAsset[] {
  const by = new Map<string, JupnetAsset>();
  for (const r of routes)
    for (const t of r.tokens) {
      const id = assetIdOf(t);
      let a = by.get(id);
      if (!a) {
        const unified = id === "USD";
        a = { id, sym: id, name: unified ? "Unified USD, from USDC" : `${t.sym} on Jupnet`, unified, home: HOME[id], routes: [] };
        by.set(id, a);
      }
      a.routes.push({ chain: r.chain, token: t });
    }
  for (const a of by.values()) a.routes.sort((x, y) => rank(a.home, x.chain.key) - rank(a.home, y.chain.key));
  const pos = (id: string) => (ASSET_ORDER.indexOf(id) < 0 ? ASSET_ORDER.length : ASSET_ORDER.indexOf(id));
  return [...by.values()].sort((a, b) => pos(a.id) - pos(b.id) || a.id.localeCompare(b.id));
}

export type RouteOption = { chain: Chain; token: Token; ok: boolean; reason?: string; home: boolean };

/** Why Jupnet cannot be received from this source at all, or null. */
export function jupnetBlocked(src: Chain): string | null {
  return src.family === "sui" ? "A Sui source can't open a Jupnet deposit yet. Pay from an EVM chain, Solana or Jupnet to receive on Jupnet." : null;
}

/** The routes of `asset` for a swap paid on `src`, in preference order, each usable or with why not. */
export function routeOptions(asset: JupnetAsset, src: Chain): RouteOption[] {
  const blocked = jupnetBlocked(src);
  return asset.routes.map(({ chain, token }) => {
    const home = chain.key === asset.home;
    if (blocked) return { chain, token, home, ok: false, reason: "Not from a Sui source" };
    if (chain.key === src.key) return { chain, token, home, ok: false, reason: `You pay on ${chain.label}: a route must be another chain` };
    return { chain, token, home, ok: true };
  });
}

/** The route used before any quote: the first usable in preference order (home chain first). */
export const defaultRoute = (asset: JupnetAsset, src: Chain): RouteOption | undefined => routeOptions(asset, src).find((o) => o.ok);

/** Why `asset` has no usable route from `src`, or null when it has one. */
export function assetUnavailable(asset: JupnetAsset, src: Chain): string | null {
  const opts = routeOptions(asset, src);
  if (opts.some((o) => o.ok)) return null;
  return jupnetBlocked(src) ?? `Only via ${opts.map((o) => o.chain.label).join(", ")}, the chain you pay on`;
}

/** One route's quote: its best `dst_amount` (null: no solver quoted it). */
export type RouteQuote = { chain: ChainKey; amount: bigint | null; decimals: number };

/** The route to use after quoting several: the best amount (compared across decimals), unless the
 *  home chain's is within `HOME_TOLERANCE_BPS` of it. Null when none was quoted. */
export function pickRoute(quotes: RouteQuote[], home?: ChainKey): ChainKey | null {
  const quoted = quotes.filter((q): q is RouteQuote & { amount: bigint } => q.amount !== null && q.amount > 0n);
  if (!quoted.length) return null;
  const dec = Math.max(...quoted.map((q) => q.decimals));
  const norm = (q: { amount: bigint; decimals: number }) => q.amount * 10n ** BigInt(dec - q.decimals);
  const best = quoted.reduce((a, b) => (norm(b) > norm(a) ? b : a));
  const h = quoted.find((q) => q.chain === home);
  if (h && norm(h) * 10_000n >= norm(best) * BigInt(10_000 - HOME_TOLERANCE_BPS)) return h.chain;
  return best.chain;
}

/** How long a fill and Jupnet's credit usually take, end to end (shown, not enforced). */
export const TYPICAL_CREDIT = "usually under a minute";
